// CombatTracker — initiative order, current-turn highlight, and turn advancement.
// Party rows show full HP/AC (a portrait chip, an HP bar, floating +/- deltas on change,
// death-save pips when down). FOE rows are REDACTED by the backend for players — no exact
// HP, no AC — so they render a fuzzy wound-bar keyed to a coarse wound tier instead of
// numbers. Foes whose conditions grant attackers advantage get a small ⬆ marker.
import { useEffect, useRef, useState } from "react";
import type { AuthUser, Combat, Combatant, Seat, WsClientMessage } from "../lib/types";
import { uid } from "../lib/format";
import { anyGrantsAdvantage, conditionTone, conditionTooltip } from "../lib/conditions";
import { PixelArt } from "./PixelArt";

// A combat row may be a full party row or a redacted foe row (wound tier only).
type Row = Combatant & { wound_tier?: string; wound_frac?: number };
interface FloatNum { uid: string; id: string; amount: number; }

export function CombatTracker({
  combat,
  busy,
  send,
  seats,
  you,
  gameId,
  art,
}: {
  combat: Combat;
  busy: boolean;
  send: (msg: WsClientMessage) => boolean;
  seats?: Seat[];
  you?: AuthUser | null;
  // Optional — enables portrait chips on party rows when supplied.
  gameId?: string;
  art?: Record<string, string>;
}) {
  // Advancing is free for AI/monster turns (they need a click to resolve). But
  // advancing past a HUMAN's turn permanently skips their action — only offer it
  // to the host (admin), and behind a confirm, so nobody silently skips a player.
  const current = combat.order.find((c) => c.is_current);
  const currentSeat = (seats ?? []).find((s) => s.character_id && s.character_id === current?.character_id);
  const currentIsHuman = !!current?.is_pc && currentSeat?.kind === "human";
  const canAdvance = !currentIsHuman || !!you?.is_admin;

  // Float +/- HP deltas off party rows on change (foes have no numbers to diff).
  const prevHp = useRef<Record<string, number>>({});
  const [floats, setFloats] = useState<FloatNum[]>([]);
  const [flashing, setFlashing] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fresh: FloatNum[] = [];
    const flash = new Set<string>();
    for (const c of combat.order as Row[]) {
      if (c.hp == null) continue; // redacted foe — no HP to track
      const prev = prevHp.current[c.id];
      if (prev != null && prev !== c.hp) {
        fresh.push({ uid: uid("fl"), id: c.id, amount: c.hp - prev });
        flash.add(c.id);
      }
      prevHp.current[c.id] = c.hp;
    }
    if (fresh.length === 0) return;
    setFloats((f) => [...f, ...fresh]);
    setFlashing(flash);
    const t = window.setTimeout(() => {
      setFloats((f) => f.filter((x) => !fresh.some((n) => n.uid === x.uid)));
      setFlashing(new Set());
    }, 1100);
    return () => window.clearTimeout(t);
  }, [combat.order]);

  function advance() {
    if (currentIsHuman && !window.confirm(`Skip ${current?.name}'s turn? Their action will be lost.`)) return;
    send({ action: "advance_combat" });
  }

  return (
    <div className="combat-panel">
      <div className="combat-head">
        <div className="ct-title">⚔️ {combat.name}</div>
        <div className="ct-round">Round {combat.round} · {combat.current_turn}’s turn</div>
      </div>
      {(combat.order as Row[]).map((c) => (
        <InitRow key={c.id} c={c} floats={floats.filter((f) => f.id === c.id)}
          flash={flashing.has(c.id)} gameId={gameId} artUrl={art?.[`character:${c.character_id ?? ""}`]} />
      ))}
      <div style={{ padding: 12, display: "flex", gap: 8 }}>
        <button className="btn btn--primary grow" disabled={busy || !canAdvance} onClick={advance}
          title={!canAdvance ? `Waiting for ${current?.name} to act — only the host can skip a player` : "Advance turn"}>
          {currentIsHuman ? "↻ Skip turn" : "↻ Advance turn"}
        </button>
      </div>
    </div>
  );
}

// Map a coarse wound tier → bar fill class + label. NO exact numbers ever (the backend
// redacts foe HP/AC out of the player payload by design).
const WOUND_LABEL: Record<string, string> = {
  unhurt: "Unhurt", scratched: "Scratched", bloodied: "Bloodied", "near-death": "Near death", down: "Down",
};

function InitRow({
  c, floats, flash, gameId, artUrl,
}: {
  c: Row;
  floats: FloatNum[];
  flash: boolean;
  gameId?: string;
  artUrl?: string;
}) {
  // A redacted foe row carries no hp/ac — render a fuzzy wound-bar; a party row keeps numbers.
  const redacted = c.hp == null;
  const isPc = !redacted && (c.is_pc || c.side === "party");
  const down = redacted ? (c.wound_tier === "down" || !c.alive) : (c.hp != null && c.hp <= 0);
  // The engine layers "dead"/"unconscious" onto downed foes — don't surface the
  // attacker-advantage hint on a creature that's already out of the fight.
  const showAdvHint = c.alive && c.side === "foe" && anyGrantsAdvantage(c.conditions);
  const effects = c.active_effects ?? [];
  const mx = c.max_hp ?? 0;
  const ratio = !redacted && mx > 0 ? (c.hp ?? 0) / mx : 0;
  const fillColor = ratio <= 0.25 ? "var(--hp-crit)" : ratio <= 0.5 ? "var(--hp-low)" : "var(--hp)";

  const tier = c.wound_tier ?? "unhurt";
  const woundFrac = typeof c.wound_frac === "number" ? c.wound_frac : 1;
  const woundMod = tier === "near-death" || tier === "down" ? " wound-bar--near-death"
    : tier === "bloodied" ? " wound-bar--bloodied" : "";

  return (
    <div className={`init-row ${c.side}${c.is_current ? " current" : ""}${!c.alive ? " dead" : ""}${flash ? " hit-flash" : ""}`}
      style={{ position: "relative" }}>
      {floats.map((f) => (
        <span key={f.uid} className={`float-num${f.amount > 0 ? " float-num--heal" : ""}`}
          style={{ position: "absolute", right: 10, top: 6, pointerEvents: "none", fontWeight: 800 }}>
          {f.amount > 0 ? `+${f.amount}` : f.amount}
        </span>
      ))}
      <div className="init-init">{c.initiative ?? "—"}</div>
      <div className="init-body">
        <div className="init-name" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {isPc && c.character_id && (
            gameId ? (
              <span style={down ? { filter: "grayscale(1)", opacity: 0.7 } : undefined}>
                <PixelArt shape="thumb" alt={c.name} liveUrl={artUrl}
                  body={{ kind: "character", game_id: gameId, character_id: c.character_id }} />
              </span>
            ) : null
          )}
          {c.name}
          {showAdvHint && (
            <span className="adv-hint" title="A condition grants attackers advantage against this foe">⬆ adv</span>
          )}
          {c.concentration && (
            <span className="chip chip--conc" title={`Concentrating on ${c.concentration}`}>◎ {c.concentration}</span>
          )}
        </div>
        <div className="init-meta">
          {redacted ? (
            <span style={{ opacity: 0.8 }}>{WOUND_LABEL[tier] ?? "—"}</span>
          ) : (
            <>
              <span>🛡 {c.ac}</span>
              <span style={{ fontFamily: "var(--mono)" }}>
                {c.alive ? `${c.hp}/${c.max_hp}` : "down"}{c.temp_hp > 0 ? ` +${c.temp_hp}` : ""}
              </span>
            </>
          )}
        </div>
        {(c.conditions.length > 0 || effects.length > 0) && (
          <div className="init-conditions">
            {effects.map((e) => (
              <span key={`e-${e}`} className="cond-chip cond-chip--good" title={conditionTooltip(e)}>🔥 {e}</span>
            ))}
            {c.conditions.map((cd) => (
              <span key={cd} className={`cond-chip cond-chip--${conditionTone(cd)}`} title={conditionTooltip(cd)}>{cd}</span>
            ))}
          </div>
        )}
        {/* Party: exact HP bar. Foe: fuzzy wound-bar (tier only, no numbers). */}
        {!redacted && c.alive && (
          <div className="init-hpbar">
            <div className="init-hpbar__fill" style={{ width: `${Math.max(0, Math.min(100, ratio * 100))}%`, background: fillColor }} />
          </div>
        )}
        {redacted && (
          <div className={`wound-bar${woundMod}`}
            style={{ height: 6, borderRadius: 3, overflow: "hidden", marginTop: 4,
                     background: "var(--panel-2, rgba(127,127,127,0.18))" }}
            title={WOUND_LABEL[tier] ?? ""}>
            <div style={{ width: `${Math.max(0, Math.min(100, woundFrac * 100))}%`, height: "100%",
                          background: woundMod ? "var(--crimson-bright, #e5484d)" : "var(--hp, #4caf50)",
                          transition: "width 300ms ease-out" }} />
          </div>
        )}
        {isPc && down && (
          <div className="death-pips" title="Making death saves" style={{ display: "flex", gap: 4, marginTop: 4 }}>
            {[0, 1, 2].map((i) => (
              <span key={i} aria-hidden
                style={{ width: 8, height: 8, borderRadius: "50%", border: "1.5px solid var(--crimson-bright, #e5484d)",
                         animation: "hearthPulse 1.1s ease-in-out infinite" }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
